/**
 * Photo-ID request history for the claim panel (ticket #659).
 *
 * Staff saw only the FIRST request date ("ID request sent on 03/12/2025") while
 * the client had been chased many times since. This turns the client record's
 * fields into one plain line, e.g.
 *
 *   ID requested: first 3 Dec 2025 · last 13 Oct 2026 · 10 sent
 *
 * What the client record fields mean (photo_id_service.send_id_request_email):
 *   idRequestSentDate       the first request (kept on every resend)
 *   idRequestLastSentDate   the latest request
 *   idRequestReminderCount  every ID email sent, the first one included
 *
 * Paused reminders (#667 stopgap) park idRequestLastSentDate at
 * 2099-01-01T00:00:00 so the chaser skips the client. That is never a real
 * date, so it reads "Reminders paused" and the real last date comes from the
 * pause record instead.
 *
 * Dates are parsed by hand: backend timestamps are ISO, sometimes with no zone
 * (naive UTC), which `new Date()` would read as local time, and `new Date()`
 * also swaps UK DD/MM/YYYY. They are shown as UK calendar dates in UK time.
 */

export interface IdRequestPause {
  reason?: string | null;
  at?: string | null;
  lastSentDateBeforePause?: string | null;
}

export interface IdRequestFields {
  idRequestSent?: boolean | null;
  idRequestSentDate?: string | null;
  idRequestLastSentDate?: string | null;
  idRequestReminderCount?: number | null;
  idRequestPause?: IdRequestPause | null;
  clientDeceased?: boolean | null;
}

export interface IdRequestSummary {
  /** "first 3 Dec 2025 · last 13 Oct 2026 · 10 sent"; null when no ID request was ever sent. */
  summary: string | null;
  /** Why no more reminders go (paused / deceased); null while they still do. */
  stopped: string | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// No real request is dated this far ahead: a date from this year on is a pause marker.
const FAR_FUTURE_YEAR = 2090;

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i;
const UK_RE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

function realCalendarDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const d = new Date(Date.UTC(year, month - 1, day));
  return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day;
}

/**
 * A backend timestamp as a Date, or null if it isn't one.
 * ISO with a zone keeps it; ISO with no zone is UTC; DD/MM/YYYY is a UK date.
 */
export function parseBackendTimestamp(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  const iso = ISO_RE.exec(text);
  if (iso) {
    const [, y, mo, d, h = '0', mi = '0', s = '0', frac = '', zone] = iso;
    if (!realCalendarDay(+y, +mo, +d) || +h > 23 || +mi > 59 || +s > 59) return null;
    let ms = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s, frac ? Number((frac + '00').slice(0, 3)) : 0);
    if (zone && zone.toUpperCase() !== 'Z') {
      const digits = zone.slice(1).replace(':', '');
      const offsetMinutes = Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4));
      ms -= (zone[0] === '-' ? -1 : 1) * offsetMinutes * 60000;
    }
    return new Date(ms);
  }
  const uk = UK_RE.exec(text);
  if (uk) {
    const [, d, mo, y] = uk;
    if (!realCalendarDay(+y, +mo, +d)) return null;
    return new Date(Date.UTC(+y, +mo - 1, +d));
  }
  return null;
}

function ukParts(date: Date): { day: number; month: number; year: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', day: 'numeric', month: 'numeric', year: 'numeric',
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find(p => p.type === type)?.value);
    const out = { day: get('day'), month: get('month'), year: get('year') };
    if (out.day && out.month && out.year) return out;
  } catch {
    // no time-zone support: fall back to UTC (at most an hour out)
  }
  return { day: date.getUTCDate(), month: date.getUTCMonth() + 1, year: date.getUTCFullYear() };
}

/** "3 Dec 2025" - the UK calendar date of a moment. */
export function formatUkDate(date: Date): string {
  const { day, month, year } = ukParts(date);
  return `${day} ${MONTHS[month - 1]} ${year}`;
}

function sameUkDay(a: Date, b: Date): boolean {
  return formatUkDate(a) === formatUkDate(b);
}

function isFarFuture(date: Date | null): boolean {
  return !!date && date.getUTCFullYear() >= FAR_FUTURE_YEAR;
}

/** The plain part of a pause reason: "Ticket #667: client believed deceased - ..." -> "client believed deceased". */
export function pauseReasonText(reason: unknown): string {
  if (typeof reason !== 'string') return '';
  return reason
    .trim()
    .replace(/^ticket\s*#?\s*\d+\s*[:\-–—]\s*/i, '')
    .split(/\s+[-–—]\s+/)[0]
    .replace(/[.\s]+$/, '')
    .trim();
}

/** True if the claim itself says the client has died (as the email composer's deceased check). */
export function claimSaysClientDeceased(
  claim: { clientDeceased?: boolean; litigationFriendDetails?: { role?: string } } | null | undefined,
): boolean {
  if (!claim) return false;
  const role = claim.litigationFriendDetails?.role;
  return claim.clientDeceased === true || role === 'executor' || role === 'administrator';
}

/** The ID-request line for a client (see the file comment). */
export function describeIdRequests(fields: IdRequestFields | null | undefined): IdRequestSummary {
  if (!fields) return { summary: null, stopped: null };

  const pause = fields.idRequestPause && typeof fields.idRequestPause === 'object' ? fields.idRequestPause : null;
  const recordedLast = parseBackendTimestamp(fields.idRequestLastSentDate);
  // The far-future date is what stops the chaser, so it alone decides "paused";
  // the pause record only supplies the reason and the real last date.
  const paused = isFarFuture(recordedLast);

  let first = parseBackendTimestamp(fields.idRequestSentDate);
  let last = paused ? parseBackendTimestamp(pause?.lastSentDateBeforePause) : recordedLast;
  if (isFarFuture(first)) first = null;      // never show a marker date as a real one
  if (isFarFuture(last)) last = null;

  const rawCount = fields.idRequestReminderCount;
  const count = typeof rawCount === 'number' && Number.isFinite(rawCount) && rawCount > 0 ? Math.floor(rawCount) : 0;

  const sent = !!fields.idRequestSent || !!first || !!last || count > 0;
  if (!sent) return { summary: null, stopped: paused ? pausedText(pause) : null };

  const parts: string[] = [];
  const twoDays = !!first && !!last && !sameUkDay(first, last);
  if (twoDays) {
    parts.push(`first ${formatUkDate(first as Date)}`, `last ${formatUkDate(last as Date)}`);
  } else {
    const only = first ?? last;
    if (only) parts.push(count > 1 && !last ? `first ${formatUkDate(only)}` : formatUkDate(only));
  }
  // The count can be lower than the dates prove (older records were reset or merged).
  if (count >= (twoDays ? 2 : 1)) parts.push(`${count} sent`);
  else if (twoDays) parts.push('at least 2 sent');

  const stopped = paused
    ? pausedText(pause)
    : fields.clientDeceased ? 'Reminders stopped – client deceased' : null;
  return { summary: parts.length ? parts.join(' · ') : 'date not recorded', stopped };
}

function pausedText(pause: IdRequestPause | null): string {
  const reason = pauseReasonText(pause?.reason);
  return reason ? `Reminders paused – ${reason}` : 'Reminders paused';
}
